import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import { Landmark, Download, PlayCircle, History } from "lucide-react";

const fmt = (n) => `£${Number(n ?? 0).toFixed(2)}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function BondManagement({ fundraisers = [], onPolicyState, canWrite = true }) {
  const [policy, setPolicy] = useState(null);
  const [policyError, setPolicyError] = useState(null);
  const [versions, setVersions] = useState([]);
  const [recon, setRecon] = useState([]);
  const [reconMonth, setReconMonth] = useState(thisMonth());
  const [ledger, setLedger] = useState(null);
  const [selectedFid, setSelectedFid] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    enabled: true, contributionType: "percentage", contributionValue: 10, exposureMonths: 6,
    cohortBasis: "validation", cancellationDeductionBasis: "full_original_commission",
    cancellationDeductionValue: 0, negativeBalancesAllowed: true,
    recoveryMethod: "future_contributions_and_matured_releases", payoutCadence: "monthly",
    maturityReleaseTiming: "after_exposure", postReleaseClawback: false,
    postMaturityCancellationTreatment: "no_deduction", effectiveDate: "",
  });

  const loadPolicy = async () => {
    setPolicyError(null);
    try {
      const [p, v] = await Promise.all([
        apiClient.get("/bond/policy"),
        apiClient.get("/bond/policy/versions"),
      ]);
      setPolicy(p.data?.policy || null);
      setVersions(v.data?.versions || []);
      onPolicyState?.(!!(p.data?.policy?.snapshot?.enabled));
    } catch (e) {
      // Package 6H — honest error state: a failed request must never be
      // presented as "no policy configured".
      setPolicyError(e?.response?.data?.detail || e?.message || "Unable to load the bond policy");
    }
  };

  useEffect(() => { loadPolicy(); }, []);

  const createPolicy = async () => {
    setBusy(true);
    try {
      const body = { ...form,
        contributionValue: Number(form.contributionValue),
        exposureMonths: Number(form.exposureMonths),
        cancellationDeductionValue: Number(form.cancellationDeductionValue),
        effectiveDate: form.effectiveDate ? new Date(form.effectiveDate).toISOString() : null };
      await apiClient.post("/bond/policy", body);
      toast.success("Bond policy activated");
      await loadPolicy();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate policy");
    } finally { setBusy(false); }
  };

  const loadRecon = async () => {
    setBusy(true);
    try {
      const res = await apiClient.get(`/bond/reconciliation?month=${reconMonth}`);
      setRecon(res.data?.rows || []);
    } catch (e) { setRecon([]); toast.error("Could not load reconciliation"); }
    finally { setBusy(false); }
  };

  const runMaturity = async () => {
    setBusy(true);
    try {
      const res = await apiClient.post("/bond/maturity/process", { asOfMonth: reconMonth });
      toast.success(`Maturity processed: ${res.data.cohortsMatured} cohort(s), ${fmt(res.data.totalPayoutDeclared)} declared`);
      await loadRecon();
    } catch (e) { toast.error(e?.response?.data?.detail || "Maturity run failed"); }
    finally { setBusy(false); }
  };

  const loadLedger = async (fid) => {
    setSelectedFid(fid);
    setLedger(null);
    if (!fid) return;
    setBusy(true);
    try {
      const res = await apiClient.get(`/bond/ledger/${fid}`);
      setLedger(res.data);
    } catch (e) { toast.error("Could not load ledger"); }
    finally { setBusy(false); }
  };

  const exportLedgerCsv = () => {
    if (!ledger?.ledger?.length) return;
    const cols = ["timestamp", "type", "amount", "balanceAfter", "cohortMonth", "maturityMonth",
      "conversionId", "campaignId", "policyVersionId", "note"];
    // Package 6G — CSV formula-injection protection
    const sanitizeCell = (s) => (/^[=+\-@\t\r]/.test(s) ? `'${s}` : s);
    const rows = ledger.ledger.map((l) => cols.map((c) => `"${sanitizeCell(String(l[c] ?? "")).replace(/"/g, '""')}"`).join(","));
    const csv = [cols.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `bond-ledger-${selectedFid}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Bond ledger exported");
  };

  const snap = policy?.snapshot || {};

  return (
    <Card className="border-slate-200 shadow-none" data-testid="bond-management-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <Landmark className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg text-slate-900">Fundraiser Bond Engine</h2>
            <p className="text-sm text-slate-500">Agency-configurable bond policies, cohorts, maturity & reconciliation</p>
          </div>
        </div>

        <Tabs defaultValue="policy">
          <TabsList data-testid="bond-subtabs">
            <TabsTrigger value="policy" data-testid="bond-tab-policy">Policy</TabsTrigger>
            <TabsTrigger value="reconciliation" data-testid="bond-tab-reconciliation" onClick={loadRecon}>Reconciliation</TabsTrigger>
            <TabsTrigger value="ledgers" data-testid="bond-tab-ledgers">Fundraiser Ledgers</TabsTrigger>
          </TabsList>

          {/* POLICY */}
          <TabsContent value="policy" className="mt-4 space-y-5">
            <div className="bg-slate-50 rounded-lg p-4" data-testid="bond-active-policy">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-slate-800">Active Policy</h3>
                {policy ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">v{policy.versionNumber} · {snap.enabled ? "enabled" : "disabled"}</span>
                  : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">none</span>}
              </div>
              {policyError ? (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4" data-testid="bond-policy-error">
                  <p className="text-sm font-medium text-red-700">Couldn't load the bond policy</p>
                  <p className="text-xs text-red-500 mt-1">{String(policyError)}</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={loadPolicy} data-testid="bond-policy-retry-btn">Retry</Button>
                </div>
              ) : policy ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <Field label="Contribution" value={snap.contributionType === "fixed" ? fmt(snap.contributionValue) : `${snap.contributionValue}%`} />
                  <Field label="Exposure" value={`${snap.exposureMonths} months`} />
                  <Field label="Cohort basis" value={snap.cohortBasis} />
                  <Field label="Effective from" value={String(policy.effectiveFrom || "").slice(0, 10)} />
                  <Field label="Cancellation basis" value={snap.cancellationDeductionBasis} />
                  <Field label="Negative balances" value={snap.negativeBalancesAllowed ? "allowed" : "not allowed"} />
                  <Field label="Post-maturity" value={snap.postMaturityCancellationTreatment} />
                  <Field label="Payout cadence" value={snap.payoutCadence || "monthly"} />
                  <Field label="Recovery method" value={snap.recoveryMethod || "future_contributions_and_matured_releases"} />
                  <Field label="Release timing" value={snap.maturityReleaseTiming || "after_exposure"} />
                  <Field label="Post-release clawback" value={snap.postReleaseClawback ? "enabled" : "none"} />
                  <Field label="Status" value={snap.enabled ? "enabled" : "disabled"} />
                </div>
              ) : <p className="text-sm text-slate-400">No bond policy configured for this agency yet.</p>}
            </div>

            {/* History */}
            <div>
              <div className="flex items-center gap-2 mb-2"><History className="h-4 w-4 text-slate-500" /><h3 className="font-semibold text-slate-800 text-sm">Policy History (append-only)</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bond-policy-history">
                  <thead><tr className="border-b text-left text-slate-500"><th className="pb-2 pr-3">Version</th><th className="pb-2 pr-3">Status</th><th className="pb-2 pr-3">Contribution</th><th className="pb-2 pr-3">Exposure</th><th className="pb-2 pr-3">Effective from</th></tr></thead>
                  <tbody>
                    {versions.length === 0 ? <tr><td colSpan={5} className="py-3 text-slate-400">No versions.</td></tr> :
                      versions.map((v) => (
                        <tr key={v.id} className="border-b border-slate-100" data-testid={`bond-policy-version-${v.versionNumber}`}>
                          <td className="py-2 pr-3 font-medium">v{v.versionNumber}</td>
                          <td className="py-2 pr-3">{v.status}</td>
                          <td className="py-2 pr-3">{v.snapshot?.contributionType === "fixed" ? fmt(v.snapshot?.contributionValue) : `${v.snapshot?.contributionValue}%`}</td>
                          <td className="py-2 pr-3">{v.snapshot?.exposureMonths}m</td>
                          <td className="py-2 pr-3 text-slate-500">{String(v.effectiveFrom || "").slice(0, 10)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Create / activate — write roles only (owner / platform owner) */}
            {canWrite && (
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-slate-800 text-sm mb-3">Activate New Policy Version (prospective)</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div><Label className="text-xs">Contribution type</Label>
                  <Select value={form.contributionType} onValueChange={(v) => setForm({ ...form, contributionType: v })}>
                    <SelectTrigger data-testid="bond-form-contribution-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="fixed">Fixed £</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Contribution value</Label>
                  <Input type="number" step="0.01" min="0" value={form.contributionValue} data-testid="bond-form-contribution-value"
                    onChange={(e) => setForm({ ...form, contributionValue: e.target.value })} /></div>
                <div><Label className="text-xs">Exposure (months)</Label>
                  <Input type="number" min="0" value={form.exposureMonths} data-testid="bond-form-exposure"
                    onChange={(e) => setForm({ ...form, exposureMonths: e.target.value })} /></div>
                <div><Label className="text-xs">Cohort basis</Label>
                  <Select value={form.cohortBasis} onValueChange={(v) => setForm({ ...form, cohortBasis: v })}>
                    <SelectTrigger data-testid="bond-form-cohort-basis"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="submission">Submission</SelectItem><SelectItem value="validation">Validation</SelectItem><SelectItem value="first_collection">First collection</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Cancellation basis</Label>
                  <Select value={form.cancellationDeductionBasis} onValueChange={(v) => setForm({ ...form, cancellationDeductionBasis: v })}>
                    <SelectTrigger data-testid="bond-form-cancellation-basis"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_original_commission">Full original commission</SelectItem>
                      <SelectItem value="retained_amount">Retained amount</SelectItem>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed £</SelectItem>
                    </SelectContent>
                  </Select></div>
                {(form.cancellationDeductionBasis === "percentage" || form.cancellationDeductionBasis === "fixed") && (
                  <div><Label className="text-xs">{form.cancellationDeductionBasis === "percentage" ? "Deduction %" : "Deduction £"}</Label>
                    <Input type="number" step="0.01" min="0" value={form.cancellationDeductionValue} data-testid="bond-form-deduction-value"
                      onChange={(e) => setForm({ ...form, cancellationDeductionValue: e.target.value })} /></div>
                )}
                <div><Label className="text-xs">Effective date</Label>
                  <Input type="date" value={form.effectiveDate} data-testid="bond-form-effective-date"
                    onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} /></div>
                <div><Label className="text-xs">Negative balances</Label>
                  <Select value={String(form.negativeBalancesAllowed)} onValueChange={(v) => setForm({ ...form, negativeBalancesAllowed: v === "true" })}>
                    <SelectTrigger data-testid="bond-form-negative"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="true">Allowed</SelectItem><SelectItem value="false">Not allowed</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Post-maturity cancellation</Label>
                  <Select value={form.postMaturityCancellationTreatment} onValueChange={(v) => setForm({ ...form, postMaturityCancellationTreatment: v })}>
                    <SelectTrigger data-testid="bond-form-post-maturity"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="no_deduction">No deduction</SelectItem><SelectItem value="deduct">Deduct</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Policy status</Label>
                  <Select value={String(form.enabled)} onValueChange={(v) => setForm({ ...form, enabled: v === "true" })}>
                    <SelectTrigger data-testid="bond-form-enabled"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="true">Enabled</SelectItem><SelectItem value="false">Disabled</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Recovery method</Label>
                  <Select value={form.recoveryMethod} onValueChange={(v) => setForm({ ...form, recoveryMethod: v })}>
                    <SelectTrigger data-testid="bond-form-recovery-method"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="future_contributions_and_matured_releases">Future contributions & matured releases</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Payout cadence</Label>
                  <Select value={form.payoutCadence} onValueChange={(v) => setForm({ ...form, payoutCadence: v })}>
                    <SelectTrigger data-testid="bond-form-payout-cadence"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="monthly">Monthly (with payroll)</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Maturity release timing</Label>
                  <Select value={form.maturityReleaseTiming} onValueChange={(v) => setForm({ ...form, maturityReleaseTiming: v })}>
                    <SelectTrigger data-testid="bond-form-release-timing"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="after_exposure">After exposure period</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Post-release clawback</Label>
                  <Select value={String(form.postReleaseClawback)} onValueChange={(v) => setForm({ ...form, postReleaseClawback: v === "true" })}>
                    <SelectTrigger data-testid="bond-form-clawback"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="false">None (released funds are final)</SelectItem><SelectItem value="true">Clawback allowed</SelectItem></SelectContent>
                  </Select></div>
              </div>
              <Button className="mt-4" onClick={createPolicy} disabled={busy} data-testid="bond-activate-policy-btn">
                {busy ? "Activating…" : "Activate Policy"}
              </Button>
            </div>
            )}
          </TabsContent>

          {/* RECONCILIATION */}
          <TabsContent value="reconciliation" className="mt-4 space-y-4">
            <div className="flex items-end gap-3 flex-wrap">
              <div><Label className="text-xs">Month</Label>
                <Input type="month" value={reconMonth} className="w-40" data-testid="bond-recon-month"
                  onChange={(e) => setReconMonth(e.target.value)} /></div>
              <Button variant="outline" onClick={loadRecon} disabled={busy} data-testid="bond-load-recon-btn">Load reconciliation</Button>
              {canWrite && (
              <Button onClick={runMaturity} disabled={busy} data-testid="bond-run-maturity-btn">
                <PlayCircle className="h-4 w-4 mr-1" /> Run maturity ({reconMonth})
              </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bond-recon-table">
                <thead><tr className="border-b text-left text-slate-500">
                  <th className="pb-2 pr-3">Fundraiser</th><th className="pb-2 pr-3 text-right">Opening</th>
                  <th className="pb-2 pr-3 text-right">Contributions</th><th className="pb-2 pr-3 text-right">Deductions</th>
                  <th className="pb-2 pr-3 text-right">Gross matured</th><th className="pb-2 pr-3 text-right">Offset</th>
                  <th className="pb-2 pr-3 text-right">Net payout</th><th className="pb-2 pr-3 text-right">Closing</th>
                </tr></thead>
                <tbody>
                  {recon.length === 0 ? <tr><td colSpan={8} className="py-4 text-slate-400 text-center">No reconciliation rows for this month.</td></tr> :
                    recon.map((r) => {
                      const name = fundraisers.find((f) => f.id === r.fundraiserId)?.name || r.fundraiserId;
                      const neg = Number(r.closingBalance) < 0;
                      return (
                        <tr key={r.fundraiserId} className="border-b border-slate-100" data-testid={`bond-recon-row-${r.fundraiserId}`}>
                          <td className="py-2 pr-3 font-medium text-slate-800">{name}</td>
                          <td className="py-2 pr-3 text-right">{fmt(r.openingBalance)}</td>
                          <td className="py-2 pr-3 text-right text-emerald-700">{fmt(r.newContributions)}</td>
                          <td className="py-2 pr-3 text-right text-red-600">{fmt(r.cancellationDeductions)}</td>
                          <td className="py-2 pr-3 text-right">{fmt(r.grossMaturedBond)}</td>
                          <td className="py-2 pr-3 text-right text-amber-600">{fmt(r.negativeBalanceOffset)}</td>
                          <td className="py-2 pr-3 text-right font-semibold text-emerald-700">{fmt(r.netPayout)}</td>
                          <td className={`py-2 pr-3 text-right font-bold ${neg ? "text-red-600" : "text-slate-900"}`}>{fmt(r.closingBalance)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400">Matured bond payouts appear as lines on the existing Statements view & evidence explorer.</p>
          </TabsContent>

          {/* LEDGERS */}
          <TabsContent value="ledgers" className="mt-4 space-y-4">
            <div className="flex items-end gap-3 flex-wrap">
              <div><Label className="text-xs">Fundraiser</Label>
                <Select value={selectedFid} onValueChange={loadLedger}>
                  <SelectTrigger className="w-64" data-testid="bond-ledger-fundraiser-select"><SelectValue placeholder="Select fundraiser" /></SelectTrigger>
                  <SelectContent>
                    {fundraisers.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <Button variant="outline" onClick={exportLedgerCsv} disabled={!ledger?.ledger?.length} data-testid="bond-export-ledger-btn">
                <Download className="h-4 w-4 mr-1" /> Export ledger CSV
              </Button>
              {ledger && <span className="text-sm text-slate-600">Balance: <span className={`font-bold ${Number(ledger.balance) < 0 ? "text-red-600" : "text-slate-900"}`} data-testid="bond-ledger-balance">{fmt(ledger.balance)}</span></span>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bond-ledger-detail-table">
                <thead><tr className="border-b text-left text-slate-500">
                  <th className="pb-2 pr-3">Date</th><th className="pb-2 pr-3">Type</th><th className="pb-2 pr-3">Cohort</th>
                  <th className="pb-2 pr-3 text-right">Amount</th><th className="pb-2 pr-3">Conversion</th><th className="pb-2 pr-3">Policy ver.</th>
                </tr></thead>
                <tbody>
                  {!ledger ? <tr><td colSpan={6} className="py-4 text-slate-400 text-center">Select a fundraiser to trace their bond entries.</td></tr> :
                    ledger.ledger.length === 0 ? <tr><td colSpan={6} className="py-4 text-slate-400 text-center">No bond entries.</td></tr> :
                    ledger.ledger.map((l) => (
                      <tr key={l.id} className="border-b border-slate-100" data-testid={`bond-ledger-detail-${l.id}`}>
                        <td className="py-2 pr-3 text-slate-500">{String(l.timestamp || "").slice(0, 10)}</td>
                        <td className="py-2 pr-3">{l.type}</td>
                        <td className="py-2 pr-3 text-slate-500">{l.cohortMonth || "—"}</td>
                        <td className={`py-2 pr-3 text-right ${Number(l.amount) < 0 ? "text-red-600" : "text-slate-800"}`}>{Number(l.amount) === 0 ? "—" : fmt(l.amount)}</td>
                        <td className="py-2 pr-3 text-xs text-slate-400 font-mono">{l.conversionId ? String(l.conversionId).slice(0, 10) : "—"}</td>
                        <td className="py-2 pr-3 text-xs text-slate-400 font-mono">{l.policyVersionId ? String(l.policyVersionId).slice(-8) : "—"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="font-medium text-slate-800 capitalize">{String(value ?? "—").replace(/_/g, " ")}</p>
    </div>
  );
}
